import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../services/api_service';
import { API } from '../App';
import { 
  LayoutDashboard, 
  BarChart3, 
  Database, 
  Target, 
  AlertTriangle, 
  Settings2, 
  Bot, 
  Users, 
  FileText, 
  BookOpen, 
  LogOut, 
  Upload, 
  Leaf,
  Shield, 
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Building2,
  TrendingUp,
  FileBarChart,
  Layers,
  HelpCircle,
  Zap,
  Brain,
  Share2,
  Bell,
  Crown,
  User,
  FlaskConical,
  Wrench,
  Play,
  Pause,
  XCircle,
  Eye,
  Loader,
  RefreshCw,
  Plus,
  Settings
} from 'lucide-react';

// Status Badge
const StatusBadge = ({ status }) => {
  const colors = {
    draft: 'bg-slate-100 text-slate-600',
    running: 'bg-emerald-100 text-emerald-700',
    paused: 'bg-amber-100 text-amber-700',
    completed: 'bg-blue-100 text-blue-700',
    archived: 'bg-slate-100 text-slate-400'
  };
  
  const icons = {
    draft: Settings,
    running: Play,
    paused: Pause,
    completed: CheckCircle,
    archived: XCircle
  };
  
  const Icon = icons[status] || Settings;
  
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full flex items-center gap-1 ${colors[status] || colors.draft}`}>
      <Icon size={12} />
      {status?.toUpperCase()}
    </span>
  );
};

// Experiment Card
const ExperimentCard = ({ experiment, onStart, onPause, onComplete, onViewResults }) => {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h4 className="font-bold text-slate-900">{experiment.name}</h4>
          <p className="text-sm text-slate-500 mt-1">{experiment.description}</p>
        </div>
        <StatusBadge status={experiment.status} />
      </div>
      
      <div className="grid grid-cols-3 gap-4 my-4 py-3 border-y border-slate-100">
        <div className="text-center">
          <p className="text-lg font-bold text-slate-900">{experiment.variant_count}</p>
          <p className="text-xs text-slate-500">Variants</p>
        </div>
        <div className="text-center">
          <p className="text-lg font-bold text-slate-900">{(experiment.traffic_allocation * 100).toFixed(0)}%</p>
          <p className="text-xs text-slate-500">Traffic</p>
        </div>
        <div className="text-center">
          <p className="text-lg font-bold text-slate-900">{experiment.duration_days || '∞'}</p>
          <p className="text-xs text-slate-500">Days</p>
        </div>
      </div>
      
      <div className="flex items-center gap-2 mb-4 text-sm">
        <span className="font-mono bg-slate-100 text-slate-600 px-2 py-0.5 rounded text-xs">
          {experiment.agent_name}
        </span>
        <span className="text-slate-400">•</span>
        <span className="text-slate-500">{experiment.variant_type}</span>
        <span className="text-slate-400">•</span>
        <span className="text-emerald-600 font-medium">{experiment.primary_metric}</span>
      </div>
      
      <div className="flex gap-2">
        {experiment.status === 'draft' && (
          <button
            onClick={() => onStart(experiment.experiment_id)}
            className="flex-1 py-2 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 transition-colors flex items-center justify-center gap-1"
          >
            <Play size={14} /> Start
          </button>
        )}
        {experiment.status === 'running' && (
          <>
            <button
              onClick={() => onPause(experiment.experiment_id)}
              className="flex-1 py-2 bg-amber-600 text-white text-sm rounded-lg hover:bg-amber-700 transition-colors flex items-center justify-center gap-1"
            >
              <Pause size={14} /> Pause
            </button>
            <button
              onClick={() => onComplete(experiment.experiment_id)}
              className="flex-1 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-1"
            >
              <CheckCircle size={14} /> Complete
            </button>
          </>
        )}
        <button
          onClick={() => onViewResults(experiment.experiment_id)}
          className="px-4 py-2 bg-slate-100 text-slate-700 text-sm rounded-lg hover:bg-slate-200 transition-colors flex items-center gap-1"
        >
          <Eye size={14} /> Results
        </button>
      </div>
    </div>
  );
};

// Results Modal
const ResultsModal = ({ results, onClose }) => {
  if (!results) return null;
  
  const { experiment, variants, statistical_analysis } = results;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[80vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-900">{experiment?.name} Results</h2>
              <p className="text-sm text-slate-500 mt-1">
                Primary metric: <span className="font-medium text-emerald-600">{experiment?.primary_metric}</span>
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <XCircle size={20} className="text-slate-400" />
            </button>
          </div>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Variants Performance */}
          <div>
            <h3 className="font-bold text-slate-900 mb-4">Variant Performance</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {variants?.map(variant => (
                <div 
                  key={variant.variant_id}
                  className={`p-4 rounded-xl border-2 ${
                    variant.is_control ? 'border-slate-300 bg-slate-50' : 'border-blue-200 bg-blue-50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-bold text-slate-900">{variant.name}</h4>
                    {variant.is_control && (
                      <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded">CONTROL</span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-slate-500">Users</p>
                      <p className="font-bold text-slate-900">{variant.user_count || 0}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Events</p>
                      <p className="font-bold text-slate-900">{variant.event_count || 0}</p>
                    </div>
                  </div>
                  {variant.metrics && Object.keys(variant.metrics).length > 0 && (
                    <div className="mt-3 pt-3 border-t border-slate-200">
                      {Object.entries(variant.metrics).map(([metric, data]) => (
                        <div key={metric} className="flex items-center justify-between text-sm">
                          <span className="text-slate-500">{metric.replace('_', ' ')}</span>
                          <span className="font-bold text-slate-900">{data.mean?.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          
          {/* Statistical Analysis */}
          {statistical_analysis?.comparisons?.length > 0 && (
            <div>
              <h3 className="font-bold text-slate-900 mb-4">Statistical Analysis</h3>
              <div className="space-y-3">
                {statistical_analysis.comparisons.map((comparison, idx) => (
                  <div 
                    key={idx}
                    className={`p-4 rounded-xl border ${
                      comparison.is_significant && comparison.lift_percent > 0
                        ? 'border-emerald-200 bg-emerald-50'
                        : comparison.is_significant
                        ? 'border-red-200 bg-red-50'
                        : 'border-slate-200 bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-bold text-slate-900">{comparison.variant}</h4>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        comparison.recommendation === 'Winner'
                          ? 'bg-emerald-100 text-emerald-700'
                          : comparison.recommendation === 'Loser'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-slate-100 text-slate-600'
                      }`}>
                        {comparison.recommendation}
                      </span>
                    </div>
                    <div className="grid grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-slate-500">Control Mean</p>
                        <p className="font-bold text-slate-900">{comparison.control_mean?.toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Variant Mean</p>
                        <p className="font-bold text-slate-900">{comparison.variant_mean?.toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Lift</p>
                        <p className={`font-bold ${comparison.lift_percent > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {comparison.lift_percent > 0 ? '+' : ''}{comparison.lift_percent?.toFixed(1)}%
                        </p>
                      </div>
                      <div>
                        <p className="text-slate-500">Significant</p>
                        <p className="font-bold text-slate-900">{comparison.is_significant ? 'Yes' : 'No'}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Create Experiment Form
const CreateExperimentForm = ({ onSubmit, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    agent_name: 'esg_strategist',
    variant_type: 'prompt',
    primary_metric: 'user_satisfaction',
    traffic_allocation: 1.0,
    duration_days: 14,
    variants: [
      { name: 'Control', description: 'Current prompt', configuration: {} },
      { name: 'Variant A', description: 'New prompt', configuration: {} }
    ]
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const agents = ['esg_strategist', 'carbon_accountant', 'compliance_specialist', 'data_engineer', 'report_generator'];
  const variantTypes = ['prompt', 'model', 'temperature', 'system_message', 'configuration'];
  const metrics = ['user_satisfaction', 'task_completion', 'response_time', 'engagement', 'error_rate', 'helpfulness'];

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Experiment Name</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Agent</label>
          <select
            value={formData.agent_name}
            onChange={(e) => handleChange('agent_name', e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            {agents.map(agent => (
              <option key={agent} value={agent}>{agent.replace('_', ' ')}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
        <textarea
          value={formData.description}
          onChange={(e) => handleChange('description', e.target.value)}
          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 h-20"
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Variant Type</label>
          <select
            value={formData.variant_type}
            onChange={(e) => handleChange('variant_type', e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg"
          >
            {variantTypes.map(type => (
              <option key={type} value={type}>{type.replace('_', ' ')}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Primary Metric</label>
          <select
            value={formData.primary_metric}
            onChange={(e) => handleChange('primary_metric', e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg"
          >
            {metrics.map(metric => (
              <option key={metric} value={metric}>{metric.replace('_', ' ')}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Duration (days)</label>
          <input
            type="number"
            value={formData.duration_days}
            onChange={(e) => handleChange('duration_days', parseInt(e.target.value))}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg"
            min="1"
          />
        </div>
      </div>

      <div className="flex gap-3 pt-4">
        <button
          type="submit"
          disabled={loading}
          className="flex-1 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          {loading ? 'Creating...' : 'Create Experiment'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 transition-colors"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

// Main A/B Testing Dashboard
const ABTestingDashboard = () => {
  const [experiments, setExperiments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedResults, setSelectedResults] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchData = useCallback(async () => {
    const data = await apiFetch(`${API}/ab-testing/experiments`);
    if (data?._error) {
      console.error('Failed to fetch:', data.message);
    } else {
      setExperiments(data.experiments || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreate = async (formData) => {
    setCreating(true);
    const data = await apiFetch(`${API}/ab-testing/experiments`, { method: 'POST', body: formData });
    if (data?._error) {
      console.error('Failed to create:', data.message);
    } else {
      setShowCreateForm(false);
      fetchData();
    }
    setCreating(false);
  };

  const handleStart = async (experimentId) => {
    const data = await apiFetch(`${API}/ab-testing/experiments/${experimentId}/start`, { method: 'POST', body: {} });
    if (data?._error) {
      console.error('Failed to start:', data.message);
      return;
    }
    fetchData();
  };

  const handlePause = async (experimentId) => {
    const data = await apiFetch(`${API}/ab-testing/experiments/${experimentId}/pause`, { method: 'POST', body: {} });
    if (data?._error) {
      console.error('Failed to pause:', data.message);
      return;
    }
    fetchData();
  };

  const handleComplete = async (experimentId) => {
    const data = await apiFetch(`${API}/ab-testing/experiments/${experimentId}/complete`, { method: 'POST', body: {} });
    if (data?._error) {
      console.error('Failed to complete:', data.message);
      return;
    }
    fetchData();
  };

  const handleViewResults = async (experimentId) => {
    const data = await apiFetch(`${API}/ab-testing/experiments/${experimentId}/results`);
    if (data?._error) {
      console.error('Failed to get results:', data.message);
      return;
    }
    setSelectedResults(data);
  };

  const filteredExperiments = experiments.filter(exp => 
    statusFilter === 'all' || exp.status === statusFilter
  );

  const statusCounts = {
    all: experiments.length,
    draft: experiments.filter(e => e.status === 'draft').length,
    running: experiments.filter(e => e.status === 'running').length,
    completed: experiments.filter(e => e.status === 'completed').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader className="animate-spin text-blue-600" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ab-testing-dashboard">
      {/* Results Modal */}
      {selectedResults && (
        <ResultsModal results={selectedResults} onClose={() => setSelectedResults(null)} />
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-1">A/B Testing</h1>
            <p className="text-blue-200 text-sm">
              Test different agent prompts, models, and configurations
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchData}
              className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
            >
              <RefreshCw size={20} />
            </button>
            <button
              onClick={() => setShowCreateForm(true)}
              className="px-4 py-2 bg-white text-blue-600 rounded-lg font-medium hover:bg-blue-50 transition-colors flex items-center gap-2"
            >
              <Plus size={18} /> New Experiment
            </button>
          </div>
        </div>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-bold text-slate-900 mb-4">Create New Experiment</h3>
          <CreateExperimentForm
            onSubmit={handleCreate}
            onCancel={() => setShowCreateForm(false)}
            loading={creating}
          />
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        {Object.entries(statusCounts).map(([status, count]) => (
          <button
            key={status}
            onClick={() => setStatusFilter(status)}
            className={`p-4 rounded-xl border transition-all ${
              statusFilter === status
                ? 'border-blue-500 bg-blue-50'
                : 'border-slate-200 bg-white hover:border-blue-300'
            }`}
          >
            <p className="text-2xl font-bold text-slate-900">{count}</p>
            <p className="text-xs text-slate-500 capitalize">{status === 'all' ? 'Total' : status}</p>
          </button>
        ))}
      </div>

      {/* Experiments List */}
      {filteredExperiments.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
          <FlaskConical size={48} className="mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-medium text-slate-700">No Experiments</h3>
          <p className="text-sm text-slate-500 mb-4">Create your first A/B test to get started</p>
          <button
            onClick={() => setShowCreateForm(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            Create Experiment
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filteredExperiments.map(experiment => (
            <ExperimentCard
              key={experiment.experiment_id}
              experiment={experiment}
              onStart={handleStart}
              onPause={handlePause}
              onComplete={handleComplete}
              onViewResults={handleViewResults}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ABTestingDashboard;
